import { AppError } from '@refuelled/shared';
import { Big, toStored } from '../numeric.js';
/**
 * Household measures that mean the same volume whatever the food. Grams still
 * need a density; these only get us to millilitres.
 * UK metric spoon and cup conventions.
 */
const VOLUME_MEASURES_ML = {
    tsp: 5,
    teaspoon: 5,
    tbsp: 15,
    tablespoon: 15,
    dsp: 10,
    dessertspoon: 10,
    cup: 240,
    'fl oz': 28.413,
    pint: 568.261,
    litre: 1000,
    l: 1000,
    ml: 1,
};
/** Weight measures that are exact regardless of food. */
const WEIGHT_MEASURES_G = {
    g: 1,
    gram: 1,
    kg: 1000,
    oz: 28.3495,
    lb: 453.592,
};
function normaliseLabel(label) {
    return label.trim().toLowerCase().replace(/\.$/, '').replace(/s$/, '');
}
/**
 * Convert a user-entered quantity to grams for a specific food.
 *
 * Order of resolution, most specific first:
 *  1. a per-food measure (CoFID portion data, user-entered)
 *  2. a universal weight measure (oz, kg)
 *  3. a universal volume measure x the food's density
 *  4. "1 medium"-style counts x the food's typical unit weight
 */
export function toGrams(quantity, food) {
    const { value } = quantity;
    if (!Number.isFinite(value) || value < 0) {
        throw AppError.validation('Quantity must be a non-negative number');
    }
    if (food.per === 'item') {
        throw AppError.validation('This food is counted, not weighed, so it has no weight in grams');
    }
    if (quantity.unit === 'g')
        return toStored(value);
    if (quantity.unit === 'item') {
        if (food.typicalUnitWeightG == null) {
            throw AppError.validation('No typical weight is known for one of these');
        }
        return toStored(new Big(value).times(food.typicalUnitWeightG));
    }
    if (quantity.unit === 'ml') {
        const density = resolveDensity(food);
        return toStored(new Big(value).times(density));
    }
    const label = normaliseLabel(quantity.measureLabel ?? '');
    if (!label)
        throw AppError.validation('A measure quantity needs a measure label');
    const perFood = food.measures?.find((m) => normaliseLabel(m.label) === label);
    if (perFood)
        return toStored(new Big(value).times(perFood.grams));
    const weight = WEIGHT_MEASURES_G[label];
    if (weight !== undefined)
        return toStored(new Big(value).times(weight));
    const volumeMl = VOLUME_MEASURES_ML[label];
    if (volumeMl !== undefined) {
        const density = resolveDensity(food);
        return toStored(new Big(value).times(volumeMl).times(density));
    }
    if (COUNT_LABELS.has(label)) {
        if (food.typicalUnitWeightG == null) {
            throw AppError.validation(`No typical weight known for "${quantity.measureLabel}"`, {
                measureLabel: quantity.measureLabel,
            });
        }
        return toStored(new Big(value).times(food.typicalUnitWeightG));
    }
    throw AppError.validation(`Unrecognised measure "${quantity.measureLabel}"`, {
        measureLabel: quantity.measureLabel,
    });
}
const COUNT_LABELS = new Set([
    'medium',
    'small',
    'large',
    'each',
    'whole',
    'unit',
    'item',
    'piece',
]);
/**
 * Density in g/ml. Liquids without a stated density are treated as water,
 * which is right for stock, juice and milk to within a few per cent. Solids
 * without a density cannot be measured by volume.
 */
function resolveDensity(food) {
    if (food.densityGPerMl != null && food.densityGPerMl > 0)
        return food.densityGPerMl;
    if (food.isLiquid)
        return 1;
    throw AppError.validation('This food has no density, so it cannot be measured by volume');
}
/** Can this quantity be converted without asking the user anything? */
export function canConvert(quantity, food) {
    try {
        toGrams(quantity, food);
        return true;
    }
    catch {
        return false;
    }
}
/** Grams back to the most readable household measure for display. */
export function describeGrams(grams, food) {
    if (food.typicalUnitWeightG && grams >= food.typicalUnitWeightG * 0.75) {
        const count = grams / food.typicalUnitWeightG;
        const rounded = Math.round(count * 2) / 2;
        if (Math.abs(count - rounded) < 0.15)
            return `${rounded} x ${food.isLiquid ? 'unit' : 'medium'}`;
    }
    if (grams >= 1000)
        return `${toStored(new Big(grams).div(1000))} kg`;
    return `${Math.round(grams)} g`;
}
export const KNOWN_MEASURE_LABELS = [
    ...Object.keys(WEIGHT_MEASURES_G),
    ...Object.keys(VOLUME_MEASURES_ML),
    ...COUNT_LABELS,
];
export * from './pack.js';
export * from './serving.js';
export * from './liquids.js';
//# sourceMappingURL=index.js.map